import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Box
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Google as GoogleIcon,
  Public as YahooIcon
} from '@mui/icons-material';
import api from '../config/api';

const CalendarExportButton = ({ tripId, tripTitle, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadICal = async () => {
    try {
      setLoading(true);
      handleClose();

      const response = await api.get(`/calendar/ical/${tripId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${tripTitle.replace(/[^a-zA-Z0-9]/g, '_')}_itinerary.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading iCal:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Failed to download calendar: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const openGoogleCalendar = async () => {
    try {
      setLoading(true);
      handleClose();

      const response = await api.get(`/calendar/google/${tripId}`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error opening Google Calendar:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Failed to open Google Calendar: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const openYahooCalendar = async () => {
    try {
      setLoading(true);
      handleClose();

      const response = await api.get(`/calendar/yahoo/${tripId}`);
      window.open(response.data.url, '_blank');
    } catch (error) {
      console.error('Error opening Yahoo Calendar:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`Failed to open Yahoo Calendar: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Export to Calendar">
        <Box sx={{ position: 'relative' }}>
          <IconButton
          onClick={handleClick}
          disabled={disabled || loading}
          sx={{
            color: 'white',
            width: { xs: 56, sm: 56 },
            height: { xs: 56, sm: 56 },
            border: '2px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(15px)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.3)',
              transform: 'scale(1.1)',
              borderColor: 'rgba(255, 255, 255, 0.5)'
            },
            '&:disabled': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: 'rgba(255, 255, 255, 0.4)',
              opacity: 0.6
            },
            transition: 'all 0.3s ease'
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : <CalendarIcon sx={{ fontSize: { xs: 24, sm: 24 } }} />}
          </IconButton>
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={downloadICal} disabled={loading}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Download iCal (.ics)" />
        </MenuItem>
        
        <MenuItem onClick={openGoogleCalendar} disabled={loading}>
          <ListItemIcon>
            <GoogleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add to Google Calendar" />
        </MenuItem>
        
        <MenuItem onClick={openYahooCalendar} disabled={loading}>
          <ListItemIcon>
            <YahooIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Add to Yahoo Calendar" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default CalendarExportButton;
